import React from 'react';

import AccessTimeFilledOutlinedIcon from '@mui/icons-material/AccessTimeFilledOutlined';
import AirportShuttleOutlinedIcon from '@mui/icons-material/AirportShuttleOutlined';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CardTravelOutlinedIcon from '@mui/icons-material/CardTravelOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SimCardOutlinedIcon from '@mui/icons-material/SimCardOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import { Box, Chip, LinearProgress, Paper, Stack, Typography, alpha } from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';
import { luxLoyaltyBenefitDescriptionMap } from '~/utils/luxLoyalty';

/**
 * Explicit display order for benefits in the Société profile. Codes not in
 * this list fall to the end of the grid in their existing order.
 */
const BENEFIT_DISPLAY_ORDER: ReadonlyArray<App.LuxLoyaltyBenefitType> = [
  'airport_transfer',
  'hotel_upgrade',
  'lounge_pass',
  'family_and_friends_access',
  'esim_package',
  'tour_exp_credit',
  'travelshoot_credit',
  'discounted_insurance',
  'hidden_access',
  'early_access',
  'phone_support',
];

const BENEFIT_ORDER_INDEX: Partial<Record<App.LuxLoyaltyBenefitType, number>> = BENEFIT_DISPLAY_ORDER.reduce(
  (acc, code, idx) => {
    acc[code] = idx;
    return acc;
  },
  {} as Partial<Record<App.LuxLoyaltyBenefitType, number>>,
);

interface Props {
  account: App.LuxLoyaltyAccount;
}

/**
 * Icon by benefit code. Falls back to a generic star if a new code lands
 * before this map is updated.
 */
const BENEFIT_ICONS: Partial<Record<App.LuxLoyaltyBenefitType, React.ReactNode>> = {
  airport_transfer: <AirportShuttleOutlinedIcon fontSize="small" />,
  discounted_insurance: <SecurityOutlinedIcon fontSize="small" />,
  early_access: <AccessTimeFilledOutlinedIcon fontSize="small" />,
  hidden_access: <LockOutlinedIcon fontSize="small" />,
  lounge_pass: <LocalActivityOutlinedIcon fontSize="small" />,
  phone_support: <PhoneInTalkOutlinedIcon fontSize="small" />,
  tour_exp_credit: <CardTravelOutlinedIcon fontSize="small" />,
  travelshoot_credit: <CameraAltOutlinedIcon fontSize="small" />,
  hotel_upgrade: <HotelOutlinedIcon fontSize="small" />,
  esim_package: <SimCardOutlinedIcon fontSize="small" />,
  family_and_friends_access: <GroupOutlinedIcon fontSize="small" />,
};

function getBenefitIcon(code: App.LuxLoyaltyBenefitType): React.ReactNode {
  return BENEFIT_ICONS[code] ?? <StarOutlineOutlinedIcon fontSize="small" />;
}

function formatBenefitValue(
  value: NonNullable<App.LuxLoyaltyBenefit['details']['value']>,
): string {
  if (value.unit === 'CURRENCY' && value.currency) {
    return `${currencyFormatter(value.currency, value.amount, 2, 'code')} each`;
  }
  if (value.unit === 'PERCENTAGE') {
    return `${value.amount}% off`;
  }
  if (value.unit === 'GB') {
    return `${value.amount} GB`;
  }
  return `${value.amount}`;
}

export default function LuxLoyaltyAccountDetails({ account }: Props) {
  const benefits = [...account.benefits].sort((a, b) => {
    const aIdx = BENEFIT_ORDER_INDEX[a.code] ?? Number.MAX_SAFE_INTEGER;
    const bIdx = BENEFIT_ORDER_INDEX[b.code] ?? Number.MAX_SAFE_INTEGER;
    return aIdx - bIdx;
  });

  if (benefits.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No benefits available on this account.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
      }}
    >
      {benefits.map((benefit) => {
        const { limit, remaining, value } = benefit.details;
        const isUnlimited = limit === Infinity;
        const used = isUnlimited ? 0 : Math.max(0, limit - remaining);
        const usagePct = isUnlimited || limit === 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
        const isExhausted = !isUnlimited && remaining <= 0;

        return (
          <Paper
            key={benefit.code}
            elevation={0}
            sx={(theme) => ({
              p: 2,
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              borderLeft: 3,
              borderLeftColor: isUnlimited
                ? theme.palette.loyaltyTiers.infinite.main
                : isExhausted
                  ? theme.palette.warning.main
                  : theme.palette.primary.main,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            })}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={(theme) => ({
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                })}
              >
                {getBenefitIcon(benefit.code)}
              </Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
                {luxLoyaltyBenefitDescriptionMap.get(benefit.code) ?? benefit.code}
              </Typography>
              {isUnlimited ? (
                <Chip
                  size="small"
                  icon={<AllInclusiveIcon sx={{ fontSize: 14 }} />}
                  label="Unlimited"
                  sx={(theme) => ({
                    height: 22,
                    fontWeight: 700,
                    color: 'common.white',
                    background: theme.palette.loyaltyTiers.infinite.gradient,
                    '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                  })}
                />
              ) : (
                <Chip
                  size="small"
                  label={isExhausted ? 'Exhausted' : `${remaining}/${limit}`}
                  color={isExhausted ? 'warning' : 'default'}
                  sx={{ height: 22, fontWeight: 700 }}
                />
              )}
            </Stack>

            {!isUnlimited && limit > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={usagePct}
                  color={isExhausted ? 'warning' : 'primary'}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Used {used} of {limit}
                </Typography>
              </Box>
            )}

            {value && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="caption" color="text.secondary">
                  Value
                </Typography>
                <Chip
                  size="small"
                  label={formatBenefitValue(value)}
                  variant="outlined"
                  sx={{ height: 22 }}
                />
              </Stack>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}
